import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Button from 'common/components/Elements/Button/Button';
import ButtonLoading from 'common/components/Elements/ButtonLoading/ButtonLoading';
import useShortcut from 'common/utilities/hooks/useShortcut/useShortcut';
import { closeModalAction } from 'core/redux/actions/ui/uiActions';
import { useAppDispatch } from 'core/redux/store/configureStore';
import I18n from 'i18n/en-US';
import React, { useEffect, useState } from 'react';

interface ApplyButton {
  id: string;
  text: string;
}

interface ComponentProps {
  onApply?: (id: string) => void;
  applyText?: string | ApplyButton[];
  applyTitle?: string;
  disableApply?: boolean;
  persisting?: boolean;
  noApply?: boolean;
  leftActions?: JSX.Element;
  rightActions?: JSX.Element;
}

function ModalActions({
  onApply, disableApply, noApply, leftActions, rightActions, applyText, applyTitle, persisting,
}: ComponentProps) {
  const ref = React.createRef<HTMLDivElement>();
  const [modalId, setModalId] = useState<string | undefined>();

  const dispatch = useAppDispatch();
  const shortcutApplyEnterTrigger = useShortcut('Enter', false);
  const shortcutApplyCtrlSTrigger = useShortcut('ctrl+s', false);

  useEffect(() => {
    if (onApply && noApply !== true) {
      if (shortcutApplyEnterTrigger || shortcutApplyCtrlSTrigger) {
        onApply('apply');
      }
    }
  }, [onApply, noApply, shortcutApplyEnterTrigger, shortcutApplyCtrlSTrigger]);

  let applyButtons: ApplyButton[] = [{ id: 'apply', text: I18n.Elements.Modal.ModalActions.defaultApply }];
  if (typeof applyText === 'string') applyButtons = [{ id: 'apply', text: applyText }];
  if (Array.isArray(applyText)) applyButtons = applyText;

  useEffect(() => {
    if (ref.current) {
      const modalEl = ref.current.closest('#modalContent');
      if (modalEl) {
        setModalId((modalEl as HTMLDivElement).dataset.modalId);
      }
    }
  }, [ref]);

  return (
    <>
      <Box mt={2} mb={1}>
        <Divider />
      </Box>

      <Box ref={ref} display="flex">
        <Box flexGrow="1">
          {leftActions}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', textAlign: 'right' }}>
          {rightActions || (
            <>
              <Button onClick={() => dispatch(closeModalAction(modalId))} action="tertiary">
                {I18n.Elements.Modal.ModalActions.close}
              </Button>

              {noApply !== true && (
                <Box
                  position="relative"
                  title={applyTitle}
                >
                  {applyButtons.map((applyButton) => (
                    <Button
                      key={applyButton.id}
                      onClick={() => { if (onApply) onApply(applyButton.id); }}
                      action="primary"
                      disabled={disableApply}
                      ml
                    >
                      {applyButton.text}
                    </Button>
                  ))}
                  {persisting && <ButtonLoading />}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
}

export default ModalActions;
